import type { Bi } from 'schools/panchang/types/bilingual';

export type PanchangTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type PanchangChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: PanchangTopic[];
};

export const PANCHANG_HERO = {
  eyebrow: { en: 'Samhita · Daily timing', hi: 'संहिता · दैनिक समय' },
  titleShort: { en: 'Panchang', hi: 'पंचांग' },
  title: { en: 'Panchang', hi: 'पंचांग' },
  motto: {
    en: 'Read the five limbs - tithi, vara, nakshatra, yoga, karana - and choose auspicious time for daily life.',
    hi: 'पाँच अंग पढ़ें - तिथि, वार, नक्षत्र, योग, करण - और दैनिक जीवन हेतु शुभ समय चुनें।',
  },
  tagline: {
    en: 'Thirty-six lessons: from Panchang basics through balas, doshas, and marriage, travel, and festival muhurta.',
    hi: 'छत्तीस पाठ: पंचांग आधार से बल, दोष, विवाह, यात्रा और त्योहार मुहूर्त तक।',
  },
  intro: {
    en: 'Apply what Surya Siddhanta derives - this school teaches daily how, not orbital why.',
    hi: 'सूर्य सिद्धान्त जो निकालता है उसे लागू करें - यह स्कूल दैनिक उपयोग सिखाता है, कक्षीय गणित नहीं।',
  },
  trait: {
    en: 'Five limbs + bala + dosha = muhurta',
    hi: 'पाँच अंग + बल + दोष = मुहूर्त',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): PanchangTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const PANCHANG_CHAPTERS: PanchangChapter[] = [
  {
    id: 'introduction',
    number: 1,
    title: { en: 'Introduction', hi: 'परिचय' },
    subtitle: {
      en: 'What Panchang is and how it connects to Surya Siddhanta',
      hi: 'पंचांग क्या है और सूर्य सिद्धान्त से कैसे जुड़ता है',
    },
    icon: '📿',
    topics: [
      topic('what-is-panchang', 'What Is Panchang?', 'पंचांग क्या है?', 'Five limbs of the Hindu calendar.', 'हिंदू कालगणना के पाँच अंग।', 16, '📿'),
      topic('panchang-vs-civil-calendar', 'Panchang vs Civil Calendar', 'पंचांग बनाम सिविल कैलेंडर', 'Why Diwali shifts every year.', 'दिवाली हर साल क्यों बदलती है।', 16, '📅'),
      topic('bridge-from-surya-siddhanta', 'Bridge from Surya Siddhanta', 'सूर्य सिद्धान्त से कड़ी', 'Longitudes → limbs; Ganita → daily.', 'देशांतर → अंग; गणित → दैनिक।', 17, '🔗'),
      topic('reading-panchang-app', 'Reading a Panchang App', 'पंचांग ऐप पढ़ना', 'Decode one daily row.', 'एक दैनिक पंक्ति समझें।', 18, '📱'),
    ],
  },
  {
    id: 'vara',
    number: 2,
    title: { en: 'Vara (Weekday)', hi: 'वार' },
    subtitle: { en: 'Seven days and their graha lords', hi: 'सात वार और ग्रह स्वामी' },
    icon: '☀️',
    topics: [
      topic('vara-weekday-overview', 'Vara Overview', 'वार अवलोकन', 'Sunday through Saturday lords.', 'रवि से शनि तक स्वामी।', 16, '☀️'),
      topic('vara-in-muhurta', 'Vara in Muhurta', 'मुहूर्त में वार', 'When weekday supports or blocks.', 'वार कब सहायक या रोक।', 17, '🗓️'),
    ],
  },
  {
    id: 'tithi',
    number: 3,
    title: { en: 'Tithi', hi: 'तिथि' },
    subtitle: { en: 'Thirty lunar days, paksha, and electional groups', hi: 'तीस तिथियाँ, पक्ष, चयनात्मक समूह' },
    icon: '🌙',
    topics: [
      topic('tithi-concept', 'Tithi Concept', 'तिथि अवधारणा', '12° Sun–Moon elongation.', '12° सूर्य-चंद्र कोण।', 18, '🌙'),
      topic('shukla-krishna-paksha', 'Shukla & Krishna Paksha', 'शुक्ल और कृष्ण पक्ष', 'Waxing vs waning fortnight.', 'बढ़ता बनाम घटता पक्ष।', 17, '🌒'),
      topic('tithi-groups-nanda-rikta', 'Tithi Groups', 'तिथि समूह', 'Nanda, Bhadra, Jaya, Rikta, Purna.', 'नंदा, भद्रा, जया, रिक्ता, पूर्णा।', 18, '4️⃣'),
      topic('special-tithis-ekadashi', 'Special Tithis', 'विशेष तिथियाँ', 'Ekadashi, Amavasya, Purnima.', 'एकादशी, अमावस्या, पूर्णिमा।', 17, '🪔'),
      topic('tithi-dosha-electional', 'Tithi Dosha & Election', 'तिथि दोष और चयन', 'Rikta, Ashtami, Chaturthi awareness.', 'रिक्ता, अष्टमी, चतुर्थी जागरूकता।', 18, '⚠️'),
      topic('tithi-workshop', 'Tithi Workshop', 'तिथि कार्यशाला', 'Live elongation → tithi.', 'लाइव कोण → तिथि।', 19, '🛠️'),
    ],
  },
  {
    id: 'nakshatra',
    number: 4,
    title: { en: 'Nakshatra', hi: 'नक्षत्र' },
    subtitle: { en: 'Daily star, gana, and Tara Bala', hi: 'दैनिक नक्षत्र, गण, तारा बल' },
    icon: '⭐',
    topics: [
      topic('nakshatra-daily-panchang', 'Nakshatra in Panchang', 'पंचांग में नक्षत्र', 'Moon’s daily star group.', 'चंद्र का दैनिक तारा-समूह।', 17, '⭐'),
      topic('nakshatra-daily-bridge-ss', 'Why Nakshatra Changes Daily', 'नक्षत्र रोज क्यों बदलता', 'Link to sidereal month math.', 'नक्षत्र मास गणित कड़ी।', 16, '🔗'),
      topic('nakshatra-gana-muhurta', 'Gana & Muhurta Notes', 'गण और मुहूर्त नोट', 'Deva, Manushya, Rakshasa.', 'देव, मनुष्य, राक्षस।', 17, '📋'),
      topic('gandmool-awareness', 'Gandmool Awareness', 'गंडमूल जागरूकता', 'Sensitive birth stars - context.', 'संवेदनशील जन्म-नक्षत्र - संदर्भ।', 16, '🌿'),
      topic('navtara-tara-bala', 'Navtara & Tara Bala', 'नवतारा और तारा बल', 'Nine taras from Janma nakshatra.', 'जन्म नक्षत्र से नौ तारा।', 19, '🔢'),
    ],
  },
  {
    id: 'yoga-karana',
    number: 5,
    title: { en: 'Yoga & Karana', hi: 'योग और करण' },
    subtitle: { en: 'Twenty-seven yogas and eleven karanas', hi: '27 योग और 11 करण' },
    icon: '🧘',
    topics: [
      topic('yoga-concept', 'Yoga Concept', 'योग अवधारणा', 'Sun + Moon sum → 27 yogas.', 'सूर्य + चंद्र योग → 27 योग।', 17, '🧘'),
      topic('twenty-seven-yogas-quality', '27 Yogas Quality', '27 योग गुण', 'Auspicious vs inauspicious table.', 'शुभ बनाम अशुभ तालिका।', 18, '📊'),
      topic('karana-eleven-types', '11 Karana Types', '11 करण प्रकार', 'Chara and Sthira karanas.', 'चर और स्थिर करण।', 17, '½'),
      topic('bhadra-vishti-karana', 'Bhadra / Vishti', 'भद्रा / विष्टि', 'Vishti karana window.', 'विष्टि करण समय।', 18, '🐍'),
      topic('yoga-karana-selection', 'Yoga & Karana Selection', 'योग-करण चयन', 'Combine with tithi for muhurta.', 'मुहूर्त हेतु तिथि के साथ मिलाएँ।', 18, '✅'),
    ],
  },
  {
    id: 'balas-daily',
    number: 6,
    title: { en: 'Balas & Daily Reading', hi: 'बल और दैनिक पठन' },
    subtitle: { en: 'Chandra Bala, Tara Bala, morning routine', hi: 'चंद्र बल, तारा बल, प्रातःकालीन अभ्यास' },
    icon: '💪',
    topics: [
      topic('chandra-bala', 'Chandra Bala', 'चंद्र बल', 'Moon strength by rashi transit.', 'राशि गोचर से चंद्र बल।', 18, '🌙'),
      topic('tara-bala-daily', 'Tara Bala Daily', 'दैनिक तारा बल', 'Today’s nakshatra vs Janma.', 'आज का नक्षत्र बनाम जन्म।', 18, '⭐'),
      topic('five-limb-bala-checklist', 'Five-Limb Checklist', 'पाँच-अंग चेकलिस्ट', 'Full row green/amber/red.', 'पूर्ण पंक्ति हरा/पीला/लाल।', 17, '☑️'),
      topic('morning-panchang-routine', 'Morning Panchang Routine', 'प्रातः पंचांग अभ्यास', 'Five-minute daily drill.', 'पाँच मिनट दैनिक अभ्यास।', 16, '🌅'),
    ],
  },
  {
    id: 'doshas',
    number: 7,
    title: { en: 'Doshas', hi: 'दोष' },
    subtitle: { en: 'Rahu Kaal, Gulika, Panchaka, and more', hi: 'राहुकाल, गुलिक, पंचक आदि' },
    icon: '🚫',
    topics: [
      topic('rahu-kaal', 'Rahu Kaal', 'राहुकाल', 'Daily ~90 min avoid window.', 'दैनिक ~90 मिनट परहेज।', 17, '🌑'),
      topic('gulika-yamaganda', 'Gulika & Yamaganda', 'गुलिक और यमघण्ट', 'Secondary inauspicious slots.', 'द्वितीयक अशुभ खिड़की।', 17, '⏱️'),
      topic('panchaka-dosha', 'Panchaka Dosha', 'पंचक दोष', 'Five-nakshatra death association.', 'पाँच-नक्षत्र मृत्यु संबंध।', 16, '5️⃣'),
      topic('dagdha-gandanta-tithi-vara', 'Dagdha & Gandanta', 'दग्ध और गंडान्त', 'Burnt combos and junctions.', 'दग्ध संयोग और गंडान्त।', 18, '🔥'),
      topic('eclipse-inauspicious-windows', 'Eclipse Windows', 'ग्रहण समय', 'Sutak and electional pause.', 'सूतक और चयनात्मक विराम।', 17, '🌘'),
    ],
  },
  {
    id: 'muhurta-practice',
    number: 8,
    title: { en: 'Muhurta Practice', hi: 'मुहूर्त अभ्यास' },
    subtitle: { en: 'Marriage, home, travel, festivals', hi: 'विवाह, गृह, यात्रा, त्योहार' },
    icon: '💍',
    topics: [
      topic('muhurta-philosophy-workflow', 'Muhurta Philosophy', 'मुहूर्त दर्शन', 'Timing as planning, not fear.', 'समय = योजना, भय नहीं।', 17, '💭'),
      topic('marriage-muhurta-basics', 'Marriage Muhurta', 'विवाह मुहूर्त', 'Core checklist for lagna kundli.', 'लग्न कुंडली हेतु मूल चेकलिस्ट।', 20, '💍'),
      topic('griha-pravesh-business', 'Griha Pravesh & Business', 'गृह प्रवेश और व्यापार', 'Opening doors and shops.', 'द्वार और दुकान खोलना।', 19, '🏠'),
      topic('travel-naming-surgery', 'Travel, Naming & Surgery', 'यात्रा, नामकरण, शल्य', 'Electional for life events.', 'जीवन घटनाओं हेतु चयन।', 18, '✈️'),
      topic('festival-tithi-calendar', 'Festival Calendar', 'त्योहार कैलेंडर', 'Diwali, Ekadashi, Sankranti logic.', 'दिवाली, एकादशी, संक्रांति तर्क।', 18, '🎉'),
    ],
  },
];

export function getPanchangTopic(id: string) {
  for (const chapter of PANCHANG_CHAPTERS) {
    const t = chapter.topics.find((x) => x.id === id);
    if (t) return { topic: t, chapter };
  }
  return null;
}

export function getPanchangChapter(chapterId: string): PanchangChapter | undefined {
  return PANCHANG_CHAPTERS.find((c) => c.id === chapterId);
}

export function getPanchangChapterIndex(chapterId: string): number {
  return PANCHANG_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getPanchangTopicCount(): number {
  return PANCHANG_CHAPTERS.reduce((n, c) => n + c.topics.length, 0);
}

export function getPanchangTopicNeighbors(topicId: string): {
  prev: PanchangTopic | null;
  next: PanchangTopic | null;
} {
  const flat = PANCHANG_CHAPTERS.flatMap((c) => c.topics);
  const idx = flat.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return { prev: flat[idx - 1] ?? null, next: flat[idx + 1] ?? null };
}
